import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Divider,
  Descriptions,
  Button,
  Spin,
  Dropdown,
  Menu,
  Icon,
} from 'antd';
import {
  exportTaxBillRecordAsync,
  taxTraxTaxBillRecordClear,
} from './TaxTraxTaxBillRecord.actions';
import {
  BasicDrawer,
  TableSizeNames,
  CommonTable,
  IconTypeNames,
  Sizes,
  LOADING,
  PrintButton,
  ExportFormatNames,
  formatDate,
  formatPriceString,
} from 'common';
import {
  TaxBill,
  TaxBillPayment,
  TaxBillPaymentDetail,
  TaxBillRecord,
} from '../models';
import { AppState } from 'store';
import ButtonGroup from 'antd/lib/button/button-group';
import Column from 'antd/lib/table/Column';
import styles from '../TaxTrax.module.scss';
import globalStyles from 'common/global.module.scss';
import {
  Property,
  PropertyIdentifier,
  PropertyInformation,
} from 'common/models';
import {
  PropertyIdentifierDescriptions,
  PropertyInformationDescriptions,
  PropertyOwnerDescriptions,
} from 'common/templates';
import { TaxTraxDisclaimer } from '../templates';
import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { getAccessToken } from 'common/helpers';

const TaxTraxTaxBillRecord: React.FC<{
  taxBillRecord: TaxBillRecord;
  taxBill: TaxBill;
  property: Property;
  propertyInformation: PropertyInformation;
  propertyIdentifier: PropertyIdentifier;
  handleBillDownload: (taxBill: TaxBill, fileFormat: ExportFormatNames) => void;
}> = (props: {
  taxBillRecord: TaxBillRecord;
  taxBill: TaxBill;
  property: Property;
  propertyInformation: PropertyInformation;
  propertyIdentifier: PropertyIdentifier;
  handleBillDownload: (taxBill: TaxBill, fileFormat: ExportFormatNames) => void;
}) => {
  const dispatch = useDispatch();
  const {
    taxBillRecord,
    taxBill,
    property,
    propertyInformation,
    propertyIdentifier,
    handleBillDownload,
  } = props;
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const { loading }: any = useSelector((state: AppState) => ({
    loading: state.taxTraxTaxBillRecord.loading,
  }));

  const handleDetailsExport = React.useCallback(
    (format: string, action: 'print' | 'save') => {
      if (account && inProgress === InteractionStatus.None) {
        getAccessToken(instance, account, inProgress).then((response) => {
          if (response?.accessToken)
            dispatch(
              exportTaxBillRecordAsync(
                taxBillRecord,
                taxBill,
                property,
                propertyInformation,
                propertyIdentifier,
                format,
                action,
                response.accessToken,
              ),
            );
        });
      }
    },
    [
      dispatch,
      instance,
      account,
      inProgress,
      taxBillRecord,
      taxBill,
      property,
      propertyInformation,
      propertyIdentifier,
    ],
  );

  return (
    <BasicDrawer
      title={'Property Tax Payment Details'}
      onBack={() => dispatch(taxTraxTaxBillRecordClear())}
    >
      <Spin spinning={loading} size={Sizes.LARGE} tip={LOADING}>
        <Row type="flex" justify="space-between">
          <Col>
            {taxBill.billFlag && (
              <>
                <Button
                  type="primary"
                  icon={IconTypeNames.HTML}
                  onClick={() =>
                    handleBillDownload(taxBill, ExportFormatNames.HTML)
                  }
                  className={globalStyles.button}
                >
                  Bill Preview *
                </Button>
                <Button
                  type="primary"
                  icon={IconTypeNames.PDF}
                  onClick={() =>
                    handleBillDownload(taxBill, ExportFormatNames.PDF)
                  }
                  className={globalStyles.button}
                >
                  Bill Download *
                </Button>
                <p className={styles.table}>
                  <span style={{ color: '#40a9ff', fontWeight: 'bold' }}>
                    *
                  </span>
                  Fee applied on Bill Preview and Bill Download
                  <span style={{ color: '#40a9ff', fontWeight: 'bold' }}>
                    *
                  </span>
                </p>
              </>
            )}
          </Col>
          <Col>
            <ButtonGroup>
              <PrintButton
                size={TableSizeNames.SMALL}
                icon={IconTypeNames.PRINTER}
                onClick={() => handleDetailsExport('html', 'print')}
              />
              <Dropdown
                overlay={
                  <Menu onClick={({ key }) => handleDetailsExport(key, 'save')}>
                    <Menu.Item key="doc">
                      <Icon type="file-word" />
                      MS Word
                    </Menu.Item>
                    <Menu.Item key="pdf">
                      <Icon type="file-pdf" />
                      PDF
                    </Menu.Item>
                    <Menu.Item key="html">
                      <Icon type="html5" />
                      HTML
                    </Menu.Item>
                    <Menu.Item key="txt">
                      <Icon type="file-text" />
                      Text
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button
                  type="primary"
                  size={TableSizeNames.SMALL}
                  shape="round"
                  icon="download"
                >
                  Export&nbsp;
                  <Icon type="down" />
                </Button>
              </Dropdown>
            </ButtonGroup>
          </Col>
        </Row>
        <Divider dashed />

        <PropertyInformationDescriptions
          county={property.county}
          propertyInformation={propertyInformation}
        />
        <PropertyOwnerDescriptions owners={property.owners} />
        <PropertyIdentifierDescriptions
          propertyIdentifier={propertyIdentifier}
          accountNumber={property.apn}
        />

        {taxBill && (
          <Descriptions title="Tax Details" bordered size={'small'} column={3}>
            <Descriptions.Item label="Levy Type" span={1}>
              {taxBill.taxTypeName}
            </Descriptions.Item>
            <Descriptions.Item label="Levy Year" span={1}>
              {taxBill.displayYear}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated" span={1}>
              {formatDate(taxBill.postDate)}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Row type="flex" justify="space-between">
          <Col span={24}>
            <Descriptions
              title="Payment Details"
              bordered
              size={'small'}
            ></Descriptions>
            <CommonTable
              items={taxBillRecord.payments}
              rowKey="payPeriod"
              className={styles.table}
            >
              <Column width="10rem" title="PAY PERIOD" dataIndex="payPeriod" />
              <Column
                width="10rem"
                title="PAYMENT DETAILS"
                dataIndex="paymentDetails"
                render={(text: string, record: TaxBillPayment) =>
                  record.paymentDetails.map(
                    (paymentDetail: TaxBillPaymentDetail, index: number) => {
                      const paymentDetailDate = paymentDetail.paidDate
                        ? `Paid ${formatDate(paymentDetail.paidDate)}`
                        : '';
                      return (
                        <Row key={index}>
                          <b>{paymentDetail.name}:</b>{' '}
                          {formatPriceString(paymentDetail.amount)}{' '}
                          {paymentDetailDate}
                        </Row>
                      );
                    },
                  )
                }
              />
              <Column
                width="10rem"
                title="BALANCE DUE"
                dataIndex="balanceDue"
                render={(text: number) => formatPriceString(text)}
              />
              <Column width="10rem" title="STATUS" dataIndex="status" />
            </CommonTable>
          </Col>
        </Row>
        <Divider dashed />
        <TaxTraxDisclaimer></TaxTraxDisclaimer>
      </Spin>
    </BasicDrawer>
  );
};

export default TaxTraxTaxBillRecord;
